/**
 * Unified table column header menu — replaces both the inline sort-cycle
 * pattern and the hover-triggered column hide popover with a single
 * click-triggered popover per column.
 *
 * Popover layout — each entry is a row with a left label and right control:
 *   - "컬럼"        | column name
 *   - "숨김 옵션"   | [보이기 ✓] [숨기기] toggle  (when `onHide` is provided)
 *   - "정렬 옵션"   | per-metric ASC/DESC icon buttons (when sort is given)
 *   - extras label  | custom content (e.g. KPI source radios)
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import { ChevronUp, ChevronDown, Eye } from 'lucide-react';
import Tooltip from '@/components/Tooltip';
import { ColResizeHandle } from '@/components/ColResizeHandle';

export type SortMenuOption = {
  value: string;
  label: string;
  dir: 'asc' | 'desc';
  metric: string;
};

export function ColumnHeaderMenu({
  dataColid,
  children,
  className,
  style,
  title,
  columnRowLabel,
  sortOptions,
  sortBy,
  onSortChange,
  sortRowLabel,
  onHide,
  hideRowLabel,
  showButtonLabel,
  hideButtonLabel,
  extraSection,
  extraSectionTitle,
  ascTooltip,
  descTooltip,
  visibilityState,
  onVisibilityToggle,
  visibilityTooltip,
  onStartResize,
  resizeColId,
}: {
  dataColid: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Column name shown as the value of the first row. */
  title?: string;
  /** Left-side label for the first row (default: "컬럼" / "Column"). */
  columnRowLabel?: string;
  sortOptions?: SortMenuOption[];
  sortBy?: string;
  onSortChange?: (value: string) => void;
  /** Left-side label for the sort row (default: "정렬 옵션" / "Sort"). */
  sortRowLabel?: string;
  onHide?: () => void;
  /** Left-side label for the hide row (default: "숨김 옵션" / "Visibility"). */
  hideRowLabel?: string;
  /** Right toggle button labels (default: "보이기"/"숨기기" or "Show"/"Hide"). */
  showButtonLabel?: string;
  hideButtonLabel?: string;
  extraSection?: React.ReactNode;
  /** Left-side label for the extras row. */
  extraSectionTitle?: string;
  ascTooltip?: string;
  descTooltip?: string;
  /** When provided, the leading eye icon reflects this boolean (true=eye, false=eye-slash). */
  visibilityState?: boolean;
  /** When provided alongside `visibilityState`, the eye icon becomes clickable to toggle. */
  onVisibilityToggle?: (next: boolean) => void;
  /** Tooltip override for the visibility indicator. */
  visibilityTooltip?: string;
  /**
   * Column resize handler. When provided, a <ColResizeHandle> is rendered as a
   * direct child of the <th> (NOT inside .column-header-label) so it anchors to
   * the th's right edge — the real column boundary — instead of the label box,
   * which is inset by the th's horizontal padding. Pass this prop rather than
   * putting <ColResizeHandle> in children, or the divider lands ~16px inside the
   * column edge and gets clipped by the label's overflow.
   */
  onStartResize?: (colId: string, e: React.MouseEvent) => void;
  /** Column id passed to onStartResize, when it differs from dataColid (e.g. a
   *  header whose sort `field` is not the layout column id). Defaults to dataColid. */
  resizeColId?: string;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLTableCellElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  // Detect text truncation via ResizeObserver so Tooltip shows only when text is clipped
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Position popover as fixed to escape overflow-x:auto clipping, then adjust if needed
  useLayoutEffect(() => {
    if (!open || !ref.current) { setPopoverStyle({ visibility: 'hidden' }); return; }
    const thRect = ref.current.getBoundingClientRect();
    setPopoverStyle({
      position: 'fixed',
      top: thRect.bottom + 2,
      left: thRect.left + thRect.width / 2,
      transform: 'translateX(-50%)',
      visibility: 'hidden',
    });
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !popoverRef.current) return;
    const pRect = popoverRef.current.getBoundingClientRect();
    const thRect = ref.current!.getBoundingClientRect();
    const vw = window.innerWidth;
    let left: number = thRect.left + thRect.width / 2;
    let transform = 'translateX(-50%)';
    if (pRect.right > vw - 4) {
      left = thRect.right - pRect.width;
      transform = 'none';
    } else if (pRect.left < 4) {
      left = thRect.left;
      transform = 'none';
    }
    setPopoverStyle({ position: 'fixed', top: thRect.bottom + 2, left, transform, visibility: 'visible' });
  }, [open, popoverStyle.visibility]);

  const hasSort = !!sortOptions?.length;
  const hasExtra = extraSection != null;
  const hasHide = !!onHide;
  const hasAnyMenu = hasSort || hasExtra || hasHide;

  const activeSort = hasSort ? sortOptions!.find((o) => o.value === sortBy) : undefined;
  const isSortActive = !!activeSort;

  // Group sort options by metric so ASC/DESC sit together
  const metrics: { metric: string; label: string; asc?: SortMenuOption; desc?: SortMenuOption }[] = [];
  if (sortOptions) {
    for (const opt of sortOptions) {
      let entry = metrics.find((m) => m.metric === opt.metric);
      if (!entry) {
        entry = { metric: opt.metric, label: opt.label };
        metrics.push(entry);
      }
      entry[opt.dir] = opt;
    }
  }

  const handleSelectSort = (value: string) => {
    if (!onSortChange) return;
    if (sortBy === value) {
      onSortChange(''); // toggle off
    } else {
      onSortChange(value);
    }
    setOpen(false);
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    if (!hasAnyMenu) return;
    // Don't open menu when a column resize drag just ended
    if (document.body.hasAttribute('data-col-resizing')) return;
    if ((e.target as HTMLElement).closest('.column-header-popover, button, input, label, .col-resize-handle')) return;
    setOpen((v) => !v);
  };

  const resolvedColumnRowLabel = columnRowLabel ?? t('Column', '컬럼');
  const resolvedSortRowLabel = sortRowLabel ?? t('Sort', '정렬 옵션');
  const resolvedHideRowLabel = hideRowLabel ?? t('Visibility', '숨김 옵션');
  const resolvedShowLabel = showButtonLabel ?? t('Show', '보이기');
  const resolvedHideLabel = hideButtonLabel ?? t('Hide', '숨기기');
  const resolvedAscTooltip = ascTooltip ?? t('Ascending', '오름차순');
  const resolvedDescTooltip = descTooltip ?? t('Descending', '내림차순');
  const showColumnRow = !!title;

  return (
    <th
      ref={ref}
      data-colid={dataColid}
      className={`column-header${hasAnyMenu ? ' column-header--menu' : ''}${open ? ' column-header--open' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      role="columnheader"
      aria-sort={isSortActive ? (activeSort!.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      tabIndex={hasAnyMenu ? 0 : undefined}
      onClick={handleHeaderClick}
      onKeyDown={(e) => {
        if (!hasAnyMenu) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setOpen((v) => !v);
        } else if (e.key === 'Escape') {
          setOpen(false);
        }
      }}
    >
      <div className="column-header-label">
        {visibilityState !== undefined ? (
          onVisibilityToggle ? (
            <Tooltip content={visibilityTooltip ?? t(
              'Click to toggle whether this column is shown to advertisers.',
              '광고주에게 이 컬럼이 표시되는지 토글합니다.'
            )} placement="bottom">
              <button
                type="button"
                className="column-header-visibility-indicator column-header-visibility-indicator--toggle"
                aria-pressed={visibilityState}
                onClick={(e) => { e.stopPropagation(); onVisibilityToggle(!visibilityState); }}
              >
                <i className={`fa ${visibilityState ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden />
              </button>
            </Tooltip>  
          ) : (
            <Tooltip content={visibilityTooltip ?? (visibilityState
              ? t('Shown to advertisers', '광고주에게 표시됨')
              : t('Hidden from advertisers', '광고주에게 숨김'))} placement="bottom">
              <span
                className="column-header-visibility-indicator"
                aria-hidden
              >
                <i className={`fa ${visibilityState ? 'fa-eye' : 'fa-eye-slash'}`} />
              </span>
            </Tooltip>
          )
        ) : null}
        <span ref={textRef} className="column-header-label__text">
          {title && isTruncated ? (
            <Tooltip content={title} placement="bottom">
              <span>{children}</span>
            </Tooltip>
          ) : children}
        </span>
        {hasSort && (
          <span className="sort-arrows" aria-hidden>
            <span className={`sort-arrow-slot${isSortActive && activeSort!.dir === 'asc' ? ' sort-arrow-slot--active' : ''}`}>
              <ChevronUp className="sort-arrow-icon" />
            </span>
            <span className={`sort-arrow-slot${isSortActive && activeSort!.dir === 'desc' ? ' sort-arrow-slot--active' : ''}`}>
              <ChevronDown className="sort-arrow-icon" />
            </span>
          </span>
        )}
      </div>
      {/* Resize handle at th level (not inside the inset .column-header-label) so
          the divider sits exactly on the column boundary. */}
      {onStartResize && <ColResizeHandle colId={resizeColId ?? dataColid} onStartResize={onStartResize} />}
      {open && hasAnyMenu && (
        <div ref={popoverRef} className="column-header-popover" role="menu" style={popoverStyle} onClick={(e) => e.stopPropagation()}>
          {showColumnRow && (
            <div className="column-header-popover-row">
              <span className="column-header-popover-row-label">{resolvedColumnRowLabel}</span>
              <span className="column-header-popover-row-value column-header-popover-row-value--strong">{title}</span>
            </div>
          )}
          {hasHide && (
            <div className="column-header-popover-row">
              <span className="column-header-popover-row-label">
                <Eye className="column-header-popover-row-icon" aria-hidden />
                {resolvedHideRowLabel}
              </span>
              <div className="column-header-popover-row-value">
                <div className="filter-toggle-btns" role="group">
                  <button
                    type="button"
                    className="filter-toggle-btn filter-toggle-btn--active"
                    aria-pressed="true"
                    onClick={() => setOpen(false)}
                  >
                    {resolvedShowLabel}
                  </button>
                  <button
                    type="button"
                    className="filter-toggle-btn"
                    aria-pressed="false"
                    onClick={() => { onHide!(); setOpen(false); }}
                  >
                    {resolvedHideLabel}
                  </button>
                </div>
              </div>
            </div>
          )}
          {hasSort && (
            <div className="column-header-popover-row">
              <span className="column-header-popover-row-label">
                <i className="fa fa-sort-amount-desc" aria-hidden />
                {resolvedSortRowLabel}
              </span>
              <div className="column-header-popover-row-value">
                {metrics.map(({ metric, label: metricLabel, asc, desc }) => (
                  <div key={metric} className="column-header-popover-sort-row">
                    {metrics.length > 1 && (
                      <span className="column-header-popover-sort-metric">{metricLabel}</span>
                    )}
                    <div className="filter-toggle-btns" role="group">
                      {asc && (
                        <button
                          type="button"
                          className={`filter-toggle-btn inline-flex items-center gap-1${sortBy === asc.value ? ' filter-toggle-btn--active' : ''}`}
                          onClick={() => handleSelectSort(asc.value)}
                          aria-label={resolvedAscTooltip}
                          aria-pressed={sortBy === asc.value}
                        >
                          <ChevronUp className="column-header-popover-dir-icon" aria-hidden />
                          <span>{t('ASC', 'ASC')}</span>
                        </button>
                      )}
                      {desc && (
                        <button
                          type="button"
                          className={`filter-toggle-btn inline-flex items-center gap-1${sortBy === desc.value ? ' filter-toggle-btn--active' : ''}`}
                          onClick={() => handleSelectSort(desc.value)}
                          aria-label={resolvedDescTooltip}
                          aria-pressed={sortBy === desc.value}
                        >
                          <ChevronDown className="column-header-popover-dir-icon" aria-hidden />
                          <span>{t('DESC', 'DESC')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {hasExtra && (
            <div className="column-header-popover-row">
              {extraSectionTitle && (
                <span className="column-header-popover-row-label">{extraSectionTitle}</span>
              )}
              <div className="column-header-popover-row-value">
                {extraSection}
              </div>
            </div>
          )}
        </div>
      )}
    </th>
  );
}
